import React, { useState } from "react";
import { toast } from "react-toastify";
import * as hrmsService from "../../../services/hrmsService";
import { FormDrawer } from "../hrForm";
import { EmptyState, LoadState, PrimaryButton, SecondaryButton, Tabs } from "../hrComponents";
import { cardStyle, iconBtnStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "../hrUi";
import StructureEditor, { cleanLines, emptyLine } from "./StructureEditor";
import { formatMoney } from "./payrollHelpers";

// PAY-01, PAY-02 and the payroll settings, plus the statutory rates the platform administrator maintains (read-only here).
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
const yes = (v) => (v ? "Yes" : "—");
const KINDS = [{ label: "Earning", value: "EARNING" }, { label: "Deduction", value: "DEDUCTION" }, { label: "Reimbursement", value: "REIMBURSEMENT" }];

const componentFields = (creating) => [
  { key: "code", label: "Code", required: true, max: 20, disabled: !creating, hint: creating ? "Letters, digits and underscores, e.g. BASIC" : "The code cannot change" },
  { key: "name", label: "Name", required: true, max: 100 },
  { key: "kind", label: "Kind", type: "select", options: KINDS, disabled: !creating },
  { key: "prorate", label: "Reduced for loss of pay and part months", type: "checkbox" },
  { key: "is_basic", label: "This is the basic pay (gratuity, leave and overtime use it)", type: "checkbox" },
  { key: "pension_wages", label: "Counts towards the pension contribution salary", type: "checkbox" },
  { key: "sort_no", label: "Order on the payslip", type: "number", min: "0" },
];

function Components() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listPayComponents(true), []);
  const [edit, setEdit] = useState(undefined);
  const rows = data || [];
  const seed = async () => {
    const res = await hrmsService.seedPayComponents();
    if (res?.success) {
      toast.success(res.message);
      reload();
    } else toast.error(hrmsService.responseMessage(res));
  };
  return (
    <>
      <div className="d-flex gap-2 mb-3">
        <PrimaryButton onClick={() => setEdit(null)}>Add component</PrimaryButton>
        {rows.length === 0 ? <SecondaryButton onClick={seed}>Add the usual four (basic, housing, transport, other)</SecondaryButton> : null}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No pay components" text="Components are the lines on a payslip, like basic salary or housing allowance." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 720 }}>
              <thead>
                <tr>
                  {["Code", "Name", "Kind", "Reduced by LOP", "Basic", "Pension pay", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td style={tdStyle}>{c.code}</td>
                    <td style={tdStyle}>{c.name}</td>
                    <td style={tdStyle}>{statusLabel(c.kind)}</td>
                    <td style={tdStyle}>{yes(c.prorate)}</td>
                    <td style={tdStyle}>{yes(c.is_basic)}</td>
                    <td style={tdStyle}>{yes(c.pension_wages)}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(c.status)}>{statusLabel(c.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" style={linkBtn} onClick={() => setEdit(c)}>
                        Edit
                      </button>{" "}
                      <button
                        type="button"
                        style={linkBtn}
                        onClick={async () => {
                          const res = await hrmsService.savePayComponent(c.id, { status: c.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" });
                          if (res?.success) reload();
                          else toast.error(hrmsService.responseMessage(res));
                        }}
                      >
                        {c.status === "ACTIVE" ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={edit !== undefined}
        title={edit ? "Edit component" : "Add component"}
        fields={componentFields(!edit)}
        initial={edit ? { ...edit, prorate: !!edit.prorate, is_basic: !!edit.is_basic, pension_wages: !!edit.pension_wages } : { kind: "EARNING", prorate: true, sort_no: 100 }}
        onHide={() => setEdit(undefined)}
        onSave={(clean, raw) => {
          const body = { ...clean, prorate: !!raw.prorate, is_basic: !!raw.is_basic, pension_wages: !!raw.pension_wages };
          if (edit) delete body.code;
          return hrmsService.savePayComponent(edit ? edit.id : null, body);
        }}
        onDone={reload}
      />
    </>
  );
}

function Templates() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listSalaryTemplates(), []);
  const components = useLoad(() => hrmsService.listPayComponents(), []);
  const [edit, setEdit] = useState(undefined);
  const [lines, setLines] = useState([emptyLine()]);
  const rows = data || [];

  const open = async (t) => {
    if (!t) {
      setLines([emptyLine()]);
      setEdit(null);
      return;
    }
    try {
      const res = await hrmsService.getSalaryTemplate(t.id);
      if (res?.success === false) throw new Error(hrmsService.responseMessage(res));
      const full = res.data;
      setLines(full.lines.map((l) => ({ component_id: l.component_id, calc_type: l.calc_type, amount: l.amount ?? "", percent: l.percent ?? "", percent_of: l.percent_of || "CTC" })));
      setEdit(full);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => open(null)}>Add salary template</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No salary templates" text="A template says how a monthly CTC is split into components, so you set it once and use it for many people." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  {["Template", "Components", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id}>
                    <td style={tdStyle}>
                      {t.name}
                      {t.description ? <div style={{ fontSize: 12, color: "#6B7280" }}>{t.description}</div> : null}
                    </td>
                    <td style={tdStyle}>{t.line_count}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(t.status)}>{statusLabel(t.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" style={linkBtn} onClick={() => open(t)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={edit !== undefined}
        title={edit ? "Edit salary template" : "Add salary template"}
        width={560}
        fields={[{ key: "name", label: "Name", required: true, max: 100 }, { key: "description", label: "Description", max: 300 }]}
        initial={edit ? { name: edit.name, description: edit.description || "" } : {}}
        onHide={() => setEdit(undefined)}
        onSave={(clean) => hrmsService.saveSalaryTemplate(edit ? edit.id : null, { ...clean, lines: cleanLines(lines) })}
        onDone={reload}
      >
        <StructureEditor lines={lines} onChange={setLines} components={components.data || []} />
      </FormDrawer>
    </>
  );
}

const PASSWORD_OPTIONS = [
  { label: "Date of birth (DDMMYYYY)", value: "DOB" },
  { label: "Employee code", value: "EMPLOYEE_CODE" },
  { label: "Last 4 digits of the work phone", value: "PHONE_LAST4" },
  { label: "No password", value: "NONE" },
];

function PayrollSettings() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getPayrollSettings(), []);
  const [edit, setEdit] = useState(false);
  const s = data || {};
  const row = (k, v) => (
    <div className="d-flex justify-content-between py-2" style={{ borderBottom: "1px solid #F3F4F6", fontSize: 14 }}>
      <span style={{ color: "#6B7280" }}>{k}</span>
      <strong>{v}</strong>
    </div>
  );
  return (
    <>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <div style={{ ...cardStyle, padding: "8px 18px", maxWidth: 640 }}>
          {row("Pay day of the month", s.pay_day)}
          {row("Weekly offs and holidays between two loss-of-pay days count as loss of pay", s.sandwich_rule ? "Yes" : "No")}
          {row("Hours in a working day (for overtime)", s.standard_daily_hours)}
          {row("Flag a change in net pay of at least", `${s.variance_alert_pct}%`)}
          {row("Payslip PDF password", (PASSWORD_OPTIONS.find((o) => o.value === s.payslip_password_field) || {}).label)}
          <div className="py-2">
            <PrimaryButton onClick={() => setEdit(true)}>Edit</PrimaryButton>
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#6B7280", marginTop: 8, maxWidth: 640 }}>
          The days basis (calendar days, 30 days or working days) and rounding are set in HR Settings.
        </div>
      </LoadState>
      <FormDrawer
        show={edit}
        title="Payroll settings"
        fields={[
          { key: "pay_day", label: "Pay day of the month", type: "number", min: "1", max: 31 },
          { key: "sandwich_rule", label: "Weekly offs and holidays between two loss-of-pay days also count as loss of pay", type: "checkbox" },
          { key: "standard_daily_hours", label: "Hours in a working day", type: "number", step: "0.5", min: "1" },
          { key: "variance_alert_pct", label: "Flag a change in net pay of at least (%)", type: "number", min: "1" },
          { key: "payslip_password_field", label: "Payslip PDF password", type: "select", options: PASSWORD_OPTIONS },
        ]}
        initial={{ ...s, sandwich_rule: !!s.sandwich_rule }}
        onHide={() => setEdit(false)}
        onSave={(clean, raw) => hrmsService.savePayrollSettings({ ...clean, sandwich_rule: !!raw.sandwich_rule })}
        onDone={reload}
      />
    </>
  );
}

function StatutoryRates() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getTenantStatutoryRates(), []);
  const today = new Date().toISOString().slice(0, 10);
  const current = (key) => (data?.rates || []).filter((r) => r.rate_key === key && String(r.effective_from).slice(0, 10) <= today).sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1))[0];
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 10 }}>Statutory rates are kept by the platform administrator. A payroll run refuses to work out anything whose rate is not set.</div>
      <div style={{ ...cardStyle, overflowX: "auto" }}>
        <table className="table mb-0">
          <thead>
            <tr>
              {["Rate", "Value now", "From"].map((h) => (
                <th key={h} style={thStyle}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data?.known || []).map((k) => {
              const r = current(k.key);
              return (
                <tr key={k.key}>
                  <td style={tdStyle}>
                    {k.description}
                    <div style={{ fontSize: 11, color: "#9CA3AF" }}>{k.key}</div>
                  </td>
                  <td style={tdStyle}>{r ? `${formatMoney(r.value_num)} ${k.unit === "PERCENT" ? "%" : ""}` : <span style={{ color: k.optional ? "#6B7280" : "#B91C1C" }}>{k.optional ? "Not set (optional)" : "Not set"}</span>}</td>
                  <td style={tdStyle}>{r ? String(r.effective_from).slice(0, 10) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </LoadState>
  );
}

export default function PayrollSetupTab() {
  const [tab, setTab] = useState("components");
  return (
    <>
      <Tabs tabs={[{ key: "components", label: "Pay components" }, { key: "templates", label: "Salary templates" }, { key: "settings", label: "Payroll settings" }, { key: "rates", label: "Statutory rates" }]} active={tab} onChange={setTab} />
      {tab === "components" ? <Components /> : null}
      {tab === "templates" ? <Templates /> : null}
      {tab === "settings" ? <PayrollSettings /> : null}
      {tab === "rates" ? <StatutoryRates /> : null}
    </>
  );
}
